import { walkContours } from './polygons';
import turfArea from '@turf/area';
import { polygon as turfPolygon } from '@turf/helpers';
import turfBooleanContains from '@turf/boolean-contains';
import turfUnkinkPolygon from '@turf/unkink-polygon';
import turfSimplify from '@turf/simplify';
import turfDifference from '@turf/difference';
import turfUnion from '@turf/union';
import turfIntersect from '@turf/intersect';
import turfCircle from '@turf/circle';

import { deepCopy } from '@/utils/deep';

export default class GeometryTypeUtils {
  static _circleOptions = { steps: 10, units: 'kilometers' };
  static convertFeatureGeometry(feature, newType, circleRadius) {
    let newFeature = {};
    deepCopy(feature, newFeature);
    const geometry = newFeature.geometry;
    let newGeometry = null;

    switch (newType) {
      case 'Point':
        newGeometry = this.convertToPoint(geometry);
        break;
      case 'MultiPoint':
        newGeometry = this.convertToMultiPoint(geometry);
        break;
      case 'LineString':
        newGeometry = this.convertToLineString(geometry, circleRadius);
        break;
      case 'MultiLineString':
        newGeometry = this.convertToMultiLineString(geometry, circleRadius);
        break;
      case 'Polygon':
        newGeometry = this.convertToPolygon(geometry, circleRadius);
        break;
      case 'MultiPolygon':
        newGeometry = this.convertToMultiPolygon(geometry, circleRadius);
        break;
    }
    newFeature.geometry = newGeometry;
    return newFeature;
  }

  static convertToPoint(geometryArg) {
    const geometry = this.getUnkinkPolygon(geometryArg);
    const coords = geometry.coordinates;
    let newCoords = [];

    switch (geometry.type) {
      case 'MultiPoint':
        newCoords = coords[0];
        break;
      case 'LineString':
        newCoords = coords[0];
        break;
      case 'Polygon':
        newCoords = coords[0][0];
        break;
      case 'MultiLineString':
        newCoords = coords[0][0];
        break;
      case 'MultiPolygon':
        newCoords = coords[0][0][0];
        break;
    }
    if (newCoords[0]) {
      return { type: 'Point', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  static convertToMultiPoint(geometryArg) {
    const geometry = this.getUnkinkPolygon(geometryArg);
    const coords = geometry.coordinates;
    let newCoords = [];

    switch (geometry.type) {
      case 'Point':
        newCoords = [coords];
        break;
      case 'LineString':
        newCoords = coords;
        break;
      case 'Polygon':
        for (const ring of coords) {
          newCoords = newCoords.concat(ring);
        }
        break;
      case 'MultiLineString':
        for (const line of coords) {
          newCoords = newCoords.concat(line);
        }
        break;
      case 'MultiPolygon':
        for (const poly of coords) {
          for (let ring of poly) {
            newCoords = newCoords.concat(ring);
          }
        }
        break;
    }
    if (newCoords[0]) {
      return { type: 'MultiPoint', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  static convertToLineString(geometryArg, circleRadius) {
    const geometry = this.getUnkinkPolygon(geometryArg);
    const coords = geometry.coordinates;
    let newCoords = [];

    switch (geometry.type) {
      case 'Point':
        newCoords = turfCircle(coords, circleRadius, this._circleOptions).geometry.coordinates[0];
        newCoords.splice(0, 1);
        break;
      case 'MultiPoint':
        if (coords.length === 1) {
          newCoords = turfCircle(coords[0], circleRadius, this._circleOptions).geometry.coordinates[0];
          newCoords.splice(0, 1);
        } else {
          newCoords = coords;
          this._completePart(newCoords, newCoords[0], 2);
        }
        break;
      case 'MultiLineString':
        newCoords = coords[0];
        break;
      case 'Polygon':
        newCoords = coords[0];
        newCoords.splice(newCoords.length - 1, 1);
        break;
      case 'MultiPolygon':
        newCoords = coords[0][0];
        newCoords.splice(newCoords.length - 1, 1);
        break;
      default:
        window.console.log('unknown type');
    }

    if (newCoords[0]) {
      return { type: 'LineString', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  static convertToMultiLineString(geometryArg, circleRadius) {
    const geometry = this.getUnkinkPolygon(geometryArg);
    const coords = geometry.coordinates;
    let newCoords = [];

    switch (geometry.type) {
      case 'Point':
        newCoords = turfCircle(coords, circleRadius, this._circleOptions).geometry.coordinates;
        newCoords[0].splice(0, 1);
        break;
      case 'MultiPoint':
        if (coords.length === 1) {
          newCoords = turfCircle(coords[0], circleRadius, this._circleOptions).geometry.coordinates;
          newCoords[0].splice(0, 1);
        } else {
          newCoords = [coords];
          this._completePart(newCoords, newCoords[0], 2);
        }
        break;
      case 'LineString':
        newCoords = [coords];
        break;
      case 'Polygon':
        for (let ring of coords) {
          ring.splice(ring.length - 1, 1);
          newCoords.push(ring);
        }
        break;
      case 'MultiPolygon':
        for (let part of coords) {
          for (let ring of part) {
            ring.splice(ring.length - 1, 1);
            newCoords.push(ring);
          }
        }
        break;
      default:
        window.console.log('unknown type');
    }

    if (newCoords[0]) {
      return { type: 'MultiLineString', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  static convertToPolygon(geometryArg, circleRadius) {
    const geometry = geometryArg;
    const coords = geometry.coordinates;
    let newCoords = [];
    let polygonArray = [];

    switch (geometry.type) {
      case 'Point':
        newCoords = turfCircle(coords, circleRadius, this._circleOptions).geometry.coordinates;
        break;
      case 'MultiPoint':
        if (coords.length === 1) {
          newCoords = turfCircle(coords[0], circleRadius, this._circleOptions).geometry.coordinates;
        } else {
          newCoords = [coords];
          this._completePart(newCoords, newCoords[0], 3);
        }
        break;
      case 'LineString':
        this._completePart(coords, coords[0], 3);
        newCoords = [coords];
        break;
      case 'MultiLineString':
        for (const line of coords) {
          if (line.length > 2) {
            line.push(line[0]);
            polygonArray.push(turfPolygon([line]));
          }
        }
        this.sortGeoJsonByArea(polygonArray, true);

        for (let i = 0; i < polygonArray.length - 1; i++) {
          for (let j = i + 1; j < polygonArray.length; ) {
            if (turfBooleanContains(polygonArray[i], polygonArray[j])) {
              polygonArray[i] = this.turfTransformationSimplify(polygonArray[i], polygonArray[j], 'difference');
              polygonArray.splice(j, 1);
            } else {
              j++;
            }
          }
        }

        for (let polygon of polygonArray) {
          newCoords.push(polygon.geometry.coordinates);
        }
        break;
      case 'MultiPolygon':
        newCoords = coords[0];
        break;
      case 'Multipoint':
        this._completePart(coords, coords[0], 3);
        for (const point of coords) {
          newCoords[0].push(point);
        }
        break;
    }

    if (newCoords[0]) {
      return { type: 'Polygon', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  static convertToMultiPolygon(geometryArg, circleRadius) {
    const geometry = geometryArg;
    const coords = geometry.coordinates;
    let newCoords = [];

    switch (geometry.type) {
      case 'Point':
        newCoords = [turfCircle(coords, circleRadius, this._circleOptions).geometry.coordinates];
        break;
      case 'MultiPoint':
        if (coords.length === 1) {
          newCoords = [turfCircle(coords[0], circleRadius, this._circleOptions).geometry.coordinates];
        } else {
          newCoords = [[coords]];
          this._completePart(newCoords, newCoords[0][0], 3);
        }
        break;
      case 'LineString':
        newCoords = [[coords]];
        this._completePart(newCoords, newCoords[0][0], 3);
        break;
      case 'MultiLineString':
        newCoords = [coords];
        break;
      case 'Polygon':
        newCoords = [coords];
        break;
    }
    if (newCoords[0]) {
      return { type: 'MultiPolygon', coordinates: newCoords };
    } else {
      return geometryArg;
    }
  }

  /**Завершение редактирования
   * Возвращает false, если объект вырожденный
   */
  static finishGeometry(feature) {
    walkContours(feature.geometry.coordinates, (part, index, parent, path) => {
      let ret = false;
      if (['Polygon', 'MultiPolygon'].includes(feature.geometry.type)) {
        const first = part[0];
        const last = part[part.length - 1];
        if (first && last && first[0] != last[0] && first[1] != last[1]) {
          part.push([first[0], first[1]]);
        }
        if (part.length < 4) {
          parent.splice(index, 1);
          ret = true;
        }
      } else if (['LineString', 'MultiLineString'].includes(feature.geometry.type) && part.length < 2) {
        parent.splice(index, 1);
        ret = true;
      } else if (['MultiPoint'].includes(feature.geometry.type) && part.length < 1) {
        parent.splice(index, 1);
        ret = true;
      }
      return ret;
    });
    return !!feature.geometry.coordinates[0];
  }

  static getUnkinkPolygon(geometry) {
    let resultCoordinates = [];

    switch (geometry.type) {
      case 'MultiPolygon':
        this._unkinkMultiPolygon(geometry.coordinates, resultCoordinates);
        break;
      case 'Polygon':
        geometry.type = this._unkinkPolygon(geometry.coordinates, resultCoordinates);
        break;
      default:
        resultCoordinates = geometry.coordinates;
    }
    geometry.coordinates = resultCoordinates;

    return geometry;
  }

  static _unkinkMultiPolygon(coordinates, resultCoordinates) {
    for (let polygonCoordinates of coordinates) {
      let polygonResultCoordinates = [];
      let unkinkGeomType = this._unkinkPolygon(polygonCoordinates, polygonResultCoordinates);
      switch (unkinkGeomType) {
        case 'Polygon':
          resultCoordinates.push(polygonResultCoordinates);
          break;
        case 'MultiPolygon':
          Array.prototype.push.apply(resultCoordinates, polygonResultCoordinates);
          break;
      }
    }
  }

  static _unkinkPolygon(coordinates, resultCoordinates) {
    let result = 'Polygon';

    let ringIndex = 0;
    for (let ring of coordinates) {
      //Устранение координат дублей
      this._removeDuplicates(ring);

      //Получаем из кольца FeatureCollection полигонов без самопересечений
      let ringPolygon = null;
      try {
        ringPolygon = turfPolygon([ring]);
      } catch (err) {
        continue; //Если кольцо "плохое", то игнорируем, и продолжаем работать со следующим кольцом
      }
      let unkinkedPolygons = turfUnkinkPolygon(ringPolygon);

      if (ringIndex === 0) {
        //Если кольцо основное (первое)
        if (unkinkedPolygons.features.length > 1) {
          //Если самопересечения были, то результат поменяет тип на MultuPoligon
          result = 'MultiPolygon';

          //Полученный части сортируются по возрастанию плоощади
          this.sortGeoJsonByArea(unkinkedPolygons.features);

          //Координаты добавляются в результирующий массив
          for (let feature of unkinkedPolygons.features) {
            resultCoordinates.push(feature.geometry.coordinates);
          }
        } else {
          //Если самопересечений не было, то кольцо добавляется как есть
          resultCoordinates.push(ring);
        }
      } else {
        //Если кольцо не основное
        if (result === 'MultiPolygon') {
          //Если после рассмотрения основного кольца результат стал мультиполигоном
          for (let feature of unkinkedPolygons.features) {
            //Проходим по каждому полигону после ликвидации самопересечений
            for (let resultCoordinate of resultCoordinates) {
              //Проходим по результирующим частям мультиполигона
              if (turfBooleanContains(turfPolygon(resultCoordinate), turfPolygon(feature.geometry.coordinates))) {
                //Добавляем кольцо в часть, если текущая результирующая часть содержит это кольцо
                Array.prototype.push.apply(resultCoordinate, feature.geometry.coordinates);
                break;
              }
            }
          }
        } else {
          //Если после рассмотрения основного кольца результат остался полигоном
          for (let feature of unkinkedPolygons.features) {
            //Добавляем кольца в результат
            Array.prototype.push.apply(resultCoordinates, feature.geometry.coordinates);
          }
        }
      }

      ringIndex++;
    }

    return result;
  }

  static sortGeoJsonByArea(geoJsons, desc) {
    geoJsons.sort((part1, part2) => {
      let polygon1 = turfPolygon(part1),
        polygon2 = turfPolygon(part2),
        area1 = turfArea(polygon1),
        area2 = turfArea(polygon2);
      return desc ? area2 - area1 : area1 - area2;
    });
  }

  static _removeDuplicates(coordinates) {
    let i = 0;

    while (i <= coordinates.length - 3) {
      let j = i + 1;
      while (j <= coordinates.length - 2) {
        if (coordinates[i][0] === coordinates[j][0] && coordinates[i][1] === coordinates[j][1]) {
          coordinates.splice(j, 1);
        } else {
          j++;
        }
      }
      i++;
    }
  }

  static _completePart(part, defPoint, num) {
    while (part.length < num) {
      part.push(defPoint);
    }
  }

  static turfTransformationSimplify(geoJson1, geoJson2, operation, reverse) {
    const needSimplyfyError = 'found non-noded intersection between LINESTRING';
    const maxTryCount = 5;
    const simplifyOptions = {
      tolerance: 0.000001,
      mutate: true
    };
    let result = null;
    let tryCount = 0;

    while (result === null && tryCount < maxTryCount) {
      try {
        switch (operation) {
          case 'intersect':
            result = turfIntersect(geoJson1, geoJson2);
            break;
          case 'union':
            result = turfUnion(geoJson1, geoJson2);
            break;
          case 'difference':
            result = turfDifference(geoJson1, geoJson2);
            break;
        }

        //Прекратить выполнения цикла, если не получили исключение
        tryCount = maxTryCount;
      } catch (ex) {
        if (ex.message.indexOf(needSimplyfyError) !== -1) {
          if (reverse) {
            turfSimplify(geoJson1, simplifyOptions);
          } else {
            turfSimplify(geoJson2, simplifyOptions);
          }
          tryCount++;
          simplifyOptions.tolerance *= 10;
        } else {
          tryCount = maxTryCount;
        }
      }
    }

    return result;
  }
}
